import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projectsAPI, API_BASE } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, FolderKanban, BarChart3, Database, Bot, ArrowRight, Clock, Upload, BookTemplate, MessageSquare, HardDrive, Beaker, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function DashboardPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usageStats, setUsageStats] = useState(null);
  const [creatingSample, setCreatingSample] = useState(false);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
    loadUsageStats();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await projectsAPI.list();
      setProjects(res.data.projects || []);
    } catch (e) {
      console.error('Failed to load projects:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadUsageStats = async () => {
    try {
      const res = await axios.get(`${API_BASE}/usage/stats`);
      setUsageStats(res.data);
    } catch (e) {
      console.error('Stats:', e);
    }
  };

  const handleCreateSampleProject = async () => {
    setCreatingSample(true);
    try {
      const res = await projectsAPI.createSample({ sample_id: 'sample-pk-single-dose' });
      toast.success('Sample project created with preloaded PK dataset');
      navigate(`/app/project/${res.data.id}`);
    } catch (e) {
      toast.error('Failed to create sample project');
    } finally {
      setCreatingSample(false);
    }
  };

  const stats = [
    { label: 'Active Projects', value: usageStats?.active_projects ?? projects.length, icon: FolderKanban, color: 'text-primary' },
    { label: 'Messages Today', value: usageStats?.messages_today ?? 0, icon: MessageSquare, color: 'text-chart-2' },
    { label: 'Datasets', value: usageStats?.total_datasets ?? 0, icon: Database, color: 'text-chart-3' },
    { label: 'Storage', value: `${usageStats?.storage_used_mb ?? 0} MB`, icon: HardDrive, color: 'text-accent' },
  ];

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-[1200px] mx-auto">
      {/* Greeting */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
          Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Here's an overview of your pharmacometrics workspace</p>
      </div>

      {/* Quick Actions — horizontally scrollable on mobile */}
      <div className="flex gap-2 sm:gap-3 mb-6 sm:mb-8 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-x-visible sm:flex-wrap">
        <Button onClick={() => navigate('/app/projects')} className="rounded-full h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm active:scale-[0.98] flex-shrink-0">
          <Plus className="w-4 h-4 mr-1.5" /> New Project
        </Button>
        <Button 
          variant="outline" 
          onClick={handleCreateSampleProject} 
          disabled={creatingSample}
          className="rounded-full h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm active:scale-[0.98] flex-shrink-0 border-primary/30 text-primary hover:bg-primary/5"
          data-testid="try-sample-project-btn"
        >
          {creatingSample ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Beaker className="w-4 h-4 mr-1.5" />}
          {creatingSample ? 'Creating...' : 'Try Sample Project'}
        </Button>
        <Button variant="outline" onClick={() => navigate('/app/templates')} className="rounded-full h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm active:scale-[0.98] flex-shrink-0">
          <BookTemplate className="w-4 h-4 mr-1.5" /> Templates
        </Button>
        <Button variant="outline" onClick={() => navigate('/app/projects')} className="rounded-full h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm active:scale-[0.98] flex-shrink-0">
          <Upload className="w-4 h-4 mr-1.5" /> Upload Data
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
          >
            <Card className="card-hover">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  {loading ? (
                    <Skeleton className="h-7 w-10" />
                  ) : (
                    <span className="text-2xl font-bold font-mono">{stat.value}</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">{stat.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Quick actions */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Recent Projects</h2>
        <Button
          onClick={() => navigate('/app/projects')}
          variant="ghost"
          size="sm"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          View all <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>

      {/* Projects list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FolderKanban className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">No projects yet</p>
            <Button
              onClick={() => navigate('/app/projects')}
              className="rounded-full mt-4 h-10 px-5 text-sm active:scale-[0.98]"
              data-testid="create-first-project-button"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Create Your First Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {projects.slice(0, 5).map((project, i) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card
                className="card-hover cursor-pointer"
                onClick={() => navigate(`/app/project/${project.id}`)}
                data-testid={`project-card-${i}`}
              >
                <CardContent className="py-4 px-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>{project.name}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        {project.drug_name && (
                          <span className="text-xs font-mono text-muted-foreground">{project.drug_name}</span>
                        )}
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Database className="w-3 h-3" /> {project.dataset_count || 0}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <BarChart3 className="w-3 h-3" /> {project.session_count || 0}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                        project.regulatory_target === 'fda' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'
                      }`}>
                        {project.regulatory_target?.toUpperCase()}
                      </span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
